import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { View, Text, TextInput, Pressable, FlatList, StyleSheet } from 'react-native';
import { Search, Upload, Plus, MoreHorizontal } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { PLAN_PANEL_WIDTH } from '../../theme/layout';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { useMapAreaId } from '../../hooks/useMapAreaId';
import { DockedPanel } from '../layout/DockedPanel';
import { PanelHeader } from '../layout/PanelHeader';
import { PanelStack, usePanelStack } from '../layout/PanelStack';
import { Button } from '../common/Button';
import { TextLink } from '../common/TextLink';
import { Popover } from '../common/Popover';
import { SegmentedControl } from '../common/SegmentedControl';
import { AddPersonForm } from './AddPersonForm.web';
import { AreaPeopleView } from './AreaPeopleView.web';
import { AREA_DIRECTORY_LINK_ENABLED } from '../../constants/people';
import { useConfirm } from '../common/ConfirmDialog';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { usePeopleListsStore } from '../../store/peopleListsStore';
import { useEscapeToClose } from '../../utils/escapeStack';
import {
  areaIdsForScope,
  filterListedPeople,
  flattenLists,
  listsForArea,
  personMeta,
  splitByTeam,
  type ListedPerson,
} from '../../utils/peopleLists';
import type { ParsedPeopleFile } from '../../utils/peopleImport';
import type { PeopleList } from '../../types';

// The People button's panel (Oct 2026): the signed-in user's own uploaded
// spreadsheets of people they work with. Left-docked like Layers. Everything
// here is private to the uploader. Emails and phones show as plain
// selectable text, never as mailto: or tel: links, and nothing on a list is
// ever emailed. "Add a person" types one in by hand, onto Team in a list
// called "My people". The moderators' area directory used to open from
// Assign; its link here is switched off (AREA_DIRECTORY_LINK_ENABLED).

const SKIPPED_SHOWN = 20;
const PEOPLE_PANEL_HEADING_ID = 'people-panel-heading';

type Item =
  | { kind: 'section'; key: string; label: string; count: number }
  | { kind: 'empty'; key: string; text: string }
  | { kind: 'person'; key: string; entry: ListedPerson };

const hiddenFileInputStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function MenuRow({
  label,
  onPress,
  testID,
  danger,
}: {
  label: string;
  onPress: () => void;
  testID: string;
  danger?: boolean;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole="menuitem"
      {...hoverProps}
      style={[
        styles.menuRow,
        CURSOR_POINTER,
        { backgroundColor: hovered ? colors.hoverSurface : 'transparent' },
      ]}>
      <Text style={[styles.menuLabel, { color: danger ? colors.danger : colors.text }]}>
        {label}
      </Text>
    </Pressable>
  );
}

function ListRow({
  list,
  busy,
  onRename,
  onDelete,
}: {
  list: PeopleList;
  busy: boolean;
  onRename: (name: string) => Promise<void>;
  onDelete: () => void;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(list.name);
  const anchorRef = useRef<View>(null);

  const commit = async () => {
    const next = draft.trim().slice(0, 80);
    setRenaming(false);
    if (next && next !== list.name) await onRename(next);
  };

  return (
    <View
      style={[styles.listRow, { borderColor: colors.border }]}
      testID={`people-list-${list.id}`}>
      <View style={styles.listMain}>
        {renaming ? (
          <TextInput
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={() => void commit()}
            onBlur={() => void commit()}
            autoFocus
            maxLength={80}
            testID={`people-list-${list.id}-name-input`}
            accessibilityLabel="List name"
            style={[styles.nameInput, { color: colors.text, borderColor: colors.border }]}
          />
        ) : (
          <Text style={[styles.listName, { color: colors.text }]} numberOfLines={1}>
            {list.name}
          </Text>
        )}
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          {busy ? 'Deleting...' : `${list.rowCount} ${list.rowCount === 1 ? 'person' : 'people'}`}
        </Text>
      </View>
      <Pressable
        ref={anchorRef}
        onPress={() => setMenuOpen((v) => !v)}
        disabled={busy}
        testID={`people-list-${list.id}-more`}
        accessibilityRole="button"
        accessibilityLabel={`More actions for ${list.name}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        {...hoverProps}
        style={[styles.moreButton, CURSOR_POINTER]}>
        <MoreHorizontal size={16} color={hovered ? colors.text : colors.textSecondary} />
      </Pressable>
      <Popover
        visible={menuOpen}
        anchorRef={anchorRef}
        onClose={() => setMenuOpen(false)}
        placement="bottom-end"
        gap={SPACING.xxs}
        testID={`people-list-${list.id}-menu`}
        accessibilityLabel={`${list.name} actions`}>
        <View style={styles.menu}>
          <MenuRow
            label="Rename"
            testID={`people-list-${list.id}-rename`}
            onPress={() => {
              setMenuOpen(false);
              setDraft(list.name);
              setRenaming(true);
            }}
          />
          <MenuRow
            label="Delete list"
            danger
            testID={`people-list-${list.id}-delete`}
            onPress={() => {
              setMenuOpen(false);
              onDelete();
            }}
          />
        </View>
      </Popover>
    </View>
  );
}

const PersonRow = memo(function PersonRow({
  entry,
  onToggleTeam,
}: {
  entry: ListedPerson;
  onToggleTeam: (entry: ListedPerson) => void;
}) {
  const { colors } = useTheme();
  const { person } = entry;
  const meta = personMeta(person);
  return (
    <View style={[styles.row, { borderColor: colors.border }]} testID={`people-row-${person.id}`}>
      <View style={styles.rowMain}>
        <Text style={[styles.rowName, { color: colors.text }]} numberOfLines={1}>
          {person.name}
        </Text>
        {meta.length > 0 && (
          <Text style={[styles.meta, { color: colors.textSecondary }]} numberOfLines={1}>
            {meta}
          </Text>
        )}
        {person.category && (
          <Text style={[styles.meta, { color: colors.textSecondary }]} numberOfLines={1}>
            {person.category}
          </Text>
        )}
        {person.phone && (
          <Text selectable style={[styles.contact, { color: colors.text }]}>
            {person.phone}
          </Text>
        )}
        {person.emails.map((email) => (
          // One line with an ellipsis rather than breaking mid-address. On
          // web the full address stays in the DOM, so selecting it copies all.
          <Text
            key={email}
            selectable
            numberOfLines={1}
            style={[styles.contact, { color: colors.text }]}>
            {email}
          </Text>
        ))}
      </View>
      <Button
        title={person.team ? 'Move to Keep informed' : 'Move to Team'}
        onPress={() => onToggleTeam(entry)}
        variant="secondary"
        size="xs"
        testID={`people-row-${person.id}-team`}
        accessibilityLabel={`${person.team ? 'Move to Keep informed' : 'Move to Team'}: ${person.name}`}
      />
    </View>
  );
});

function Overline({ children }: { children: string }) {
  const { colors } = useTheme();
  return <Text style={[styles.overline, { color: colors.textSecondary }]}>{children}</Text>;
}

/** What the file turned out to hold, before anything is saved. */
function PeoplePreview({
  parsed,
  areaId,
  onSaved,
}: {
  parsed: ParsedPeopleFile;
  areaId: string;
  onSaved: () => void;
}) {
  const { colors } = useTheme();
  const { pop } = usePanelStack();
  const createList = usePeopleListsStore((s) => s.createList);
  const [name, setName] = useState(parsed.listName);
  const [scope, setScope] = useState<'here' | 'everywhere'>('here');
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<{ written: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const count = parsed.people.length;
  const canSave = count > 0 && name.trim().length > 0 && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await createList(
        name.trim().slice(0, 80),
        areaIdsForScope(scope, areaId),
        parsed.people,
        (written, total) => setProgress({ written, total })
      );
      onSaved();
      pop();
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? `Couldn't save the list. ${e.message}`
          : "Couldn't save the list. Try again."
      );
      setSaving(false);
    }
  };

  return (
    <View style={styles.previewRoot} testID="people-preview">
      <View style={styles.previewScroll}>
        <FlatList
          data={[0]}
          keyExtractor={() => 'body'}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.previewContent}
          renderItem={() => (
            <View>
              <Text
                style={[styles.previewCount, { color: colors.text }]}
                testID="people-preview-count">
                {count === 1 ? '1 person ready' : `${count} people ready`}
              </Text>
              <Text style={[styles.meta, { color: colors.textSecondary }]}>
                Only you can see this list. Nobody on it gets emailed.
              </Text>

              {parsed.skipped.length > 0 && (
                <View testID="people-preview-skipped">
                  <Overline>{`Skipped rows (${parsed.skipped.length})`}</Overline>
                  {parsed.skipped.slice(0, SKIPPED_SHOWN).map((s) => (
                    <Text key={s.row} style={[styles.detail, { color: colors.text }]}>
                      {`Row ${s.row}: ${s.reason}`}
                    </Text>
                  ))}
                  {parsed.skipped.length > SKIPPED_SHOWN && (
                    <Text style={[styles.meta, { color: colors.textSecondary }]}>
                      {`and ${parsed.skipped.length - SKIPPED_SHOWN} more`}
                    </Text>
                  )}
                </View>
              )}

              {parsed.unknownColumns.length > 0 && (
                <View testID="people-preview-unknown">
                  <Overline>Columns we didn't read</Overline>
                  <Text style={[styles.detail, { color: colors.text }]}>
                    {parsed.unknownColumns.join(', ')}
                  </Text>
                </View>
              )}

              {parsed.problems.length > 0 && (
                <View testID="people-preview-problems">
                  <Overline>{`Cells we couldn't read (${parsed.problems.length})`}</Overline>
                  {parsed.problems.slice(0, SKIPPED_SHOWN).map((p, i) => (
                    <Text
                      key={`${p.row}-${p.column}-${i}`}
                      style={[styles.detail, { color: colors.text }]}>
                      {`Row ${p.row}, ${p.column}: "${p.value}". ${p.issue}.`}
                    </Text>
                  ))}
                  {parsed.problems.length > SKIPPED_SHOWN && (
                    <Text style={[styles.meta, { color: colors.textSecondary }]}>
                      {`and ${parsed.problems.length - SKIPPED_SHOWN} more`}
                    </Text>
                  )}
                </View>
              )}

              {parsed.missingColumns.length > 0 && (
                <View testID="people-preview-missing">
                  <Overline>Not in your file</Overline>
                  <Text style={[styles.detail, { color: colors.textSecondary }]}>
                    {parsed.missingColumns.join(', ')}
                  </Text>
                </View>
              )}

              <Overline>List name</Overline>
              <TextInput
                value={name}
                onChangeText={setName}
                maxLength={80}
                testID="people-preview-name"
                accessibilityLabel="List name"
                style={[styles.input, { color: colors.text, borderColor: colors.border }]}
              />

              <Overline>Show for</Overline>
              <SegmentedControl
                options={[
                  { key: 'here', label: 'This part of the map' },
                  { key: 'everywhere', label: 'Everywhere' },
                ]}
                value={scope}
                onChange={setScope}
                ariaLabel="Show for"
                role="radiogroup"
                testID="people-preview-scope"
              />

              {error && (
                <Text
                  style={[styles.error, { color: colors.danger }]}
                  testID="people-preview-error">
                  {error}
                </Text>
              )}
            </View>
          )}
        />
      </View>
      <View style={[styles.footer, { borderColor: colors.border }]}>
        <Button
          title="Cancel"
          onPress={pop}
          variant="secondary"
          size="sm"
          disabled={saving}
          style={styles.footerButton}
          testID="people-preview-cancel"
        />
        <Button
          title={
            saving && progress
              ? `Saving ${progress.written} of ${progress.total}`
              : saving
                ? 'Saving...'
                : 'Save list'
          }
          onPress={() => void handleSave()}
          variant="primary"
          size="sm"
          disabled={!canSave}
          loading={saving}
          style={styles.footerButton}
          testID="people-preview-save"
        />
      </View>
    </View>
  );
}

function PeopleRoot({ onClose }: { onClose: () => void }) {
  const { colors } = useTheme();
  const { push } = usePanelStack();
  const confirm = useConfirm();
  const uid = useAuthStore((s) => s.user?.id ?? null);
  const isModerator = useAuthStore((s) => s.isModerator);
  const lists = usePeopleListsStore((s) => s.lists);
  const listsLoaded = usePeopleListsStore((s) => s.listsLoaded);
  const peopleByList = usePeopleListsStore((s) => s.peopleByList);
  const subscribeLists = usePeopleListsStore((s) => s.subscribeLists);
  const retainPeople = usePeopleListsStore((s) => s.retainPeople);
  const setTeam = usePeopleListsStore((s) => s.setTeam);
  const renameList = usePeopleListsStore((s) => s.renameList);
  const deleteList = usePeopleListsStore((s) => s.deleteList);
  const areaId = useMapAreaId();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<React.ElementRef<typeof Pressable>>(null);
  const addRef = useRef<React.ElementRef<typeof Pressable>>(null);
  const [search, setSearch] = useState('');
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (uid) subscribeLists(uid);
  }, [uid, subscribeLists]);

  const visibleLists = useMemo(() => listsForArea(lists, areaId), [lists, areaId]);
  const visibleKey = visibleLists.map((l) => l.id).join(',');
  useEffect(() => {
    const releases = visibleKey ? visibleKey.split(',').map((id) => retainPeople(id)) : [];
    return () => releases.forEach((release) => release());
  }, [visibleKey, retainPeople]);

  const { team, informed } = useMemo(() => {
    const all = flattenLists(visibleLists, peopleByList);
    return splitByTeam(filterListedPeople(all, search));
  }, [visibleLists, peopleByList, search]);

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [
      { kind: 'section', key: 'section-team', label: 'Team', count: team.length },
    ];
    if (team.length === 0) {
      out.push({
        kind: 'empty',
        key: 'empty-team',
        text: 'No one yet. Move people here when they work with you directly.',
      });
    }
    for (const entry of team) out.push({ kind: 'person', key: entry.person.id, entry });
    out.push({
      kind: 'section',
      key: 'section-informed',
      label: 'Keep informed',
      count: informed.length,
    });
    for (const entry of informed) out.push({ kind: 'person', key: entry.person.id, entry });
    return out;
  }, [team, informed]);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setReading(true);
      try {
        // Loaded on demand so the zip and XML code stays out of the entry
        // bundle. The file is read in memory here and goes nowhere else.
        const { parsePeopleFile } = await import('../../utils/peopleImport');
        const parsed = await parsePeopleFile(file);
        push({
          key: 'preview',
          title: 'Check your list',
          triggerRef: uploadRef,
          render: () => (
            <PeoplePreview parsed={parsed} areaId={areaId} onSaved={() => setSearch('')} />
          ),
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Couldn't read that file.");
      } finally {
        setReading(false);
      }
    },
    [push, areaId]
  );

  const handleAddPerson = () =>
    push({
      key: 'add-person',
      title: 'Add a person',
      triggerRef: addRef,
      render: () => <AddPersonForm areaId={areaId} onSaved={() => setSearch('')} />,
    });

  const handleOpenDirectory = () =>
    push({
      key: 'directory',
      title: 'Area directory',
      render: () => <AreaPeopleView areaId={areaId} confirm={confirm} />,
    });

  const handleToggleTeam = useCallback(
    (entry: ListedPerson) => {
      setError(null);
      setTeam(entry.listId, entry.person.id, !entry.person.team).catch(() =>
        setError("Couldn't move that person. Try again.")
      );
    },
    [setTeam]
  );

  const handleDelete = async (list: PeopleList) => {
    const ok = await confirm({
      title: 'Delete this list?',
      message: `This permanently deletes "${list.name}" and the ${list.rowCount} ${
        list.rowCount === 1 ? 'person' : 'people'
      } on it. You can't undo this.`,
      confirmLabel: 'Delete list',
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(list.id);
    try {
      await deleteList(list.id);
    } catch {
      setError("Couldn't delete the list. Try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const hasLists = visibleLists.length > 0;

  const header = (
    <View style={styles.headerBlock}>
      {!hasLists && listsLoaded && (
        <Text style={[styles.intro, { color: colors.textSecondary }]} testID="people-empty">
          Upload a spreadsheet or CSV of the people you work with, or add them one at a time. Only
          you can see them, and nobody on a list gets emailed.
        </Text>
      )}
      <View style={styles.actions}>
        <Button
          ref={uploadRef}
          title="Upload a list"
          onPress={() => fileInputRef.current?.click()}
          variant={hasLists ? 'secondary' : 'primary'}
          size="sm"
          leftIcon={Upload}
          loading={reading}
          style={styles.action}
          testID="people-upload"
        />
        <Button
          ref={addRef}
          title="Add a person"
          onPress={handleAddPerson}
          variant="secondary"
          size="sm"
          leftIcon={Plus}
          style={styles.action}
          testID="people-add-person"
        />
      </View>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.csv,.tsv,text/csv"
        data-testid="people-file"
        aria-label="Choose a spreadsheet or CSV file"
        style={hiddenFileInputStyle}
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          // Clear so choosing the same file again still fires onChange.
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />
      {error && (
        <Text style={[styles.error, { color: colors.danger }]} testID="people-error">
          {error}
        </Text>
      )}
      {AREA_DIRECTORY_LINK_ENABLED && isModerator && (
        <View style={styles.directory}>
          <TextLink
            label="Area directory"
            onPress={handleOpenDirectory}
            testID="people-open-directory"
            textStyle={TYPOGRAPHY.caption}
          />
        </View>
      )}
      {visibleLists.map((list) => (
        <ListRow
          key={list.id}
          list={list}
          busy={deletingId === list.id}
          onRename={(name) =>
            renameList(list.id, name).catch(() => setError("Couldn't rename the list."))
          }
          onDelete={() => void handleDelete(list)}
        />
      ))}
      {hasLists && (
        <View style={[styles.searchRow, { borderColor: colors.border }]}>
          <Search size={14} color={colors.textTertiary} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search name, organization or type"
            placeholderTextColor={colors.textTertiary}
            testID="people-panel-search"
            accessibilityLabel="Search your lists"
            style={[styles.searchInput, { color: colors.text }]}
          />
        </View>
      )}
    </View>
  );

  return (
    <View style={styles.root}>
      <PanelHeader
        title="People"
        onClose={onClose}
        headingId={PEOPLE_PANEL_HEADING_ID}
        testID="people-panel-close"
      />
      <FlatList
        data={hasLists ? items : []}
        keyExtractor={(item) => item.key}
        ListHeaderComponent={header}
        initialNumToRender={20}
        windowSize={7}
        maxToRenderPerBatch={20}
        showsVerticalScrollIndicator={false}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        renderItem={({ item }) => {
          if (item.kind === 'section') {
            return (
              <View style={styles.sectionHeader} testID={`people-${item.key}`}>
                <Overline>{`${item.label} (${item.count})`}</Overline>
              </View>
            );
          }
          if (item.kind === 'empty') {
            return <Text style={[styles.meta, { color: colors.textSecondary }]}>{item.text}</Text>;
          }
          return <PersonRow entry={item.entry} onToggleTeam={handleToggleTeam} />;
        }}
      />
    </View>
  );
}

/**
 * Left-docked People panel. Mounted only while uiStore.peoplePanelOpen is
 * true (the same "mount is open" contract as Layers), after Layers in
 * app/(tabs)/index.tsx. A PanelStack holds the upload preview, so it opens
 * inside this dock rather than as a second dialog.
 */
export function PeoplePanel({
  shown = true,
  onExited,
}: {
  shown?: boolean;
  onExited?: () => void;
}) {
  const closePeoplePanel = useUIStore((s) => s.closePeoplePanel);
  useEscapeToClose(shown, closePeoplePanel);

  return (
    <DockedPanel
      side="left"
      width={PLAN_PANEL_WIDTH}
      scroll={false}
      testID="people-panel"
      ariaLabelledBy={PEOPLE_PANEL_HEADING_ID}
      shown={shown}
      onExited={onExited}>
      <PanelStack root={<PeopleRoot onClose={closePeoplePanel} />} />
    </DockedPanel>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, minHeight: 0 },
  list: { flex: 1 },
  listContent: { paddingHorizontal: SPACING.md, paddingBottom: SPACING.xxl },
  headerBlock: { gap: SPACING.sm, paddingBottom: SPACING.sm },
  intro: { ...TYPOGRAPHY.body },
  actions: { flexDirection: 'row', gap: SPACING.sm },
  action: { flex: 1 },
  directory: { alignItems: 'flex-start' },
  overline: { ...TYPOGRAPHY.overline, marginTop: SPACING.lg, marginBottom: SPACING.sm },
  sectionHeader: { marginTop: 0 },
  meta: { ...TYPOGRAPHY.bodySmall },
  detail: { ...TYPOGRAPHY.bodySmall, marginBottom: SPACING.xxs },
  contact: { ...TYPOGRAPHY.bodySmall },
  error: { ...TYPOGRAPHY.bodySmall },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
  },
  searchInput: { ...TYPOGRAPHY.body, flex: 1, paddingVertical: SPACING.sm },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: SPACING.smd,
  },
  listMain: { flex: 1, minWidth: 0 },
  listName: { ...TYPOGRAPHY.label },
  nameInput: {
    ...TYPOGRAPHY.label,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.xs,
    paddingHorizontal: SPACING.xs,
    paddingVertical: 2,
  },
  moreButton: { padding: SPACING.xs, borderRadius: BORDER_RADIUS.sm },
  menu: { padding: SPACING.xs, minWidth: 160 },
  menuRow: {
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
  },
  menuLabel: { ...TYPOGRAPHY.bodySmall },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: SPACING.smd,
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowName: { ...TYPOGRAPHY.label },
  previewRoot: { flex: 1, minHeight: 0 },
  previewScroll: { flex: 1, minHeight: 0 },
  previewContent: { paddingHorizontal: SPACING.md, paddingBottom: SPACING.lg },
  previewCount: { ...TYPOGRAPHY.h4 },
  input: {
    ...TYPOGRAPHY.body,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
  },
  footer: {
    flexDirection: 'row',
    gap: SPACING.sm,
    padding: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  footerButton: { flex: 1 },
});
